import { useEffect, useState } from "react";
import axios from "axios";
import {
    ArrowLeft,
    MessageCircle,
    RefreshCw,
    Inbox,
    Loader2,
} from "lucide-react";
import { LogoMark } from "./Nav";
import { useAuth } from "@/lib/auth";
import { SITE } from "@/lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const waForLead = (nome, telefone) => {
    const digits = (telefone || "").replace(/\D/g, "");
    const full = digits.startsWith("55") ? digits : `55${digits}`;
    const msg = `Olá ${nome || ""}! Aqui é a ${SITE.name} Soluções Eletrônicas, recebemos seu pedido de orçamento pelo site.`;
    return `https://wa.me/${full}?text=${encodeURIComponent(msg)}`;
};

const fmtDate = (iso) => {
    try {
        return new Date(iso).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });
    } catch (e) {
        return "";
    }
};

const Gate = () => {
    const { login, checking } = useAuth();
    return (
        <div
            data-testid="painel-login-gate"
            className="min-h-screen bg-ink flex items-center justify-center px-5"
        >
            <div className="clip-corner border border-white/10 bg-panel p-9 sm:p-12 max-w-md w-full text-center">
                <div className="flex justify-center">
                    <LogoMark className="h-14 w-auto" />
                </div>
                <h1 className="mt-6 font-display text-2xl font-bold text-white">
                    Área do dono
                </h1>
                <p className="mt-3 text-sm leading-relaxed text-slate-400">
                    Este painel mostra os orçamentos pedidos pelo site. Entre
                    com a sua conta Google para ver os pedidos recebidos.
                </p>
                <button
                    data-testid="painel-login-button"
                    onClick={login}
                    disabled={checking}
                    className="mt-8 flex w-full items-center justify-center gap-3 rounded-full border border-white/20 px-6 py-4 font-hud text-xs font-bold uppercase tracking-[0.18em] text-slate-100 hover:border-volt hover:text-volt disabled:opacity-60 transition-colors duration-200"
                >
                    {checking ? (
                        <Loader2 size={16} className="animate-spin" />
                    ) : (
                        <svg width="15" height="15" viewBox="0 0 24 24" aria-hidden="true">
                            <path fill="#4285F4" d="M23.5 12.3c0-.9-.1-1.6-.2-2.3H12v4.5h6.5c-.1 1-.8 2.6-2.4 3.7l3.7 2.9c2.3-2.1 3.7-5.2 3.7-8.8z" />
                            <path fill="#34A853" d="M12 24c3.2 0 5.9-1.1 7.9-2.9l-3.7-2.9c-1 .7-2.4 1.2-4.2 1.2-3.2 0-5.9-2.1-6.8-5H1.4v3C3.4 21.4 7.4 24 12 24z" />
                            <path fill="#FBBC05" d="M5.2 14.4c-.2-.7-.4-1.5-.4-2.4s.2-1.7.4-2.4v-3H1.4C.5 8.2 0 10 0 12s.5 3.8 1.4 5.4l3.8-3z" />
                            <path fill="#EA4335" d="M12 4.8c1.8 0 3 .8 3.7 1.4l3.3-3.2C17.9 1.2 15.2 0 12 0 7.4 0 3.4 2.6 1.4 6.6l3.8 3c.9-2.9 3.6-4.8 6.8-4.8z" />
                        </svg>
                    )}
                    Entrar com Google
                </button>
                <a
                    href="/"
                    data-testid="painel-back-link-gate"
                    className="mt-5 inline-block font-hud text-[10px] uppercase tracking-[0.2em] text-slate-500 hover:text-volt transition-colors duration-200"
                >
                    Voltar ao site
                </a>
            </div>
        </div>
    );
};

const PainelPage = () => {
    const { user, checking, logout } = useAuth();
    const [leads, setLeads] = useState(null);
    const [loading, setLoading] = useState(false);
    const [forbidden, setForbidden] = useState(false);

    const fetchLeads = () => {
        setLoading(true);
        axios
            .get(`${API}/contatos`, { withCredentials: true })
            .then((res) => {
                setLeads(res.data);
                setForbidden(false);
            })
            .catch(() => {
                setForbidden(true);
                setLeads([]);
            })
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        if (user) fetchLeads();
    }, [user]);

    if (checking) {
        return (
            <div className="min-h-screen bg-ink flex items-center justify-center">
                <Loader2 size={28} className="animate-spin text-volt" />
            </div>
        );
    }

    if (!user) return <Gate />;

    return (
        <div
            data-testid="painel-page"
            className="min-h-screen bg-ink text-slate-100"
        >
            <header className="border-b border-white/10 bg-panel/60 backdrop-blur-xl">
                <div className="mx-auto max-w-6xl px-5 lg:px-8 h-[72px] flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <LogoMark className="h-10 w-auto" />
                        <span className="leading-none">
                            <span className="block font-display font-bold tracking-[0.22em] text-white text-lg">
                                {SITE.name}
                            </span>
                            <span className="block font-hud text-[9px] uppercase tracking-[0.3em] text-volt mt-1">
                                Painel do dono
                            </span>
                        </span>
                    </div>
                    <div className="flex items-center gap-3">
                        {user.picture && (
                            <img
                                src={user.picture}
                                alt=""
                                referrerPolicy="no-referrer"
                                data-testid="painel-user-avatar"
                                className="h-8 w-8 rounded-full border border-volt/40 object-cover"
                            />
                        )}
                        <a
                            href="/"
                            data-testid="painel-back-link"
                            className="hidden sm:inline-flex items-center gap-2 rounded-full border border-white/20 px-4 py-2 font-hud text-[10px] uppercase tracking-[0.18em] text-slate-300 hover:border-volt hover:text-volt transition-colors duration-200"
                        >
                            <ArrowLeft size={13} />
                            Voltar ao site
                        </a>
                        <button
                            data-testid="painel-logout-button"
                            onClick={logout}
                            className="rounded-full border border-white/20 px-4 py-2 font-hud text-[10px] uppercase tracking-[0.18em] text-slate-300 hover:border-volt hover:text-volt transition-colors duration-200"
                        >
                            Sair
                        </button>
                    </div>
                </div>
            </header>

            <main className="mx-auto max-w-6xl px-5 lg:px-8 py-12">
                <div className="flex flex-wrap items-center justify-between gap-4">
                    <div>
                        <h1 className="font-display text-2xl sm:text-3xl font-bold text-white">
                            Orçamentos recebidos
                        </h1>
                        <p
                            data-testid="painel-leads-count"
                            className="mt-2 font-hud text-[11px] uppercase tracking-[0.25em] text-volt"
                        >
                            {forbidden
                                ? "Sem acesso aos pedidos"
                                : `${leads?.length ?? 0} pedido${leads?.length === 1 ? "" : "s"} no total`}
                        </p>
                    </div>
                    <button
                        data-testid="painel-refresh-button"
                        onClick={fetchLeads}
                        disabled={loading}
                        className="inline-flex items-center gap-2 rounded-full border border-white/20 px-5 py-2.5 font-hud text-[10px] uppercase tracking-[0.18em] text-slate-200 hover:border-volt hover:text-volt disabled:opacity-60 transition-colors duration-200"
                    >
                        <RefreshCw size={13} className={loading ? "animate-spin" : ""} />
                        Atualizar
                    </button>
                </div>

                {forbidden && (
                    <div className="mt-8 rounded-2xl border border-mint/30 bg-mint/5 p-5 text-sm text-slate-300">
                        Não foi possível carregar os pedidos com esta sessão.
                        Tente sair e entrar novamente com a sua conta Google.
                    </div>
                )}

                {!forbidden && leads?.length === 0 && (
                    <div
                        data-testid="painel-empty-state"
                        className="mt-10 rounded-2xl border border-white/10 bg-panel p-12 text-center"
                    >
                        <Inbox size={32} className="mx-auto text-slate-500" />
                        <p className="mt-4 font-display text-lg font-bold text-white">
                            Nenhum orçamento ainda
                        </p>
                        <p className="mt-2 text-sm text-slate-400">
                            Assim que alguém pedir orçamento pelo site, o pedido
                            aparece aqui automaticamente.
                        </p>
                    </div>
                )}

                {!forbidden && leads?.length > 0 && (
                    <div
                        data-testid="painel-leads-list"
                        className="mt-8 grid sm:grid-cols-2 gap-4 pb-16"
                    >
                        {leads.map((lead) => (
                            <div
                                key={lead._id || lead.id}
                                data-testid="painel-lead-card"
                                className="clip-corner-sm border border-white/10 bg-panel p-6 hover:border-volt/40 transition-colors duration-300"
                            >
                                <div className="flex items-start justify-between gap-3">
                                    <div>
                                        <p className="font-display text-lg font-bold text-white">
                                            {lead.nome}
                                        </p>
                                        <p className="font-hud text-[10px] uppercase tracking-[0.2em] text-slate-500 mt-1">
                                            {fmtDate(lead.criado_em)}
                                        </p>
                                    </div>
                                    <span className="shrink-0 rounded-full border border-volt/30 bg-volt/10 px-3 py-1 font-hud text-[9px] uppercase tracking-[0.15em] text-volt">
                                        {lead.servico}
                                    </span>
                                </div>
                                <p className="mt-4 text-sm text-slate-300">
                                    {lead.telefone}
                                    {lead.condominio ? ` · ${lead.condominio}` : ""}
                                </p>
                                {lead.mensagem && (
                                    <p className="mt-3 text-sm leading-relaxed text-slate-400 border-l-2 border-volt/30 pl-3">
                                        {lead.mensagem}
                                    </p>
                                )}
                                <a
                                    href={waForLead(lead.nome, lead.telefone)}
                                    target="_blank"
                                    rel="noreferrer"
                                    data-testid="painel-lead-whatsapp"
                                    className="mt-5 inline-flex items-center gap-2 rounded-full bg-mint/10 border border-mint/40 px-4 py-2 font-hud text-[10px] font-bold uppercase tracking-[0.15em] text-mint hover:bg-mint hover:text-ink transition-colors duration-200"
                                >
                                    <MessageCircle size={13} strokeWidth={2.4} />
                                    Responder no WhatsApp
                                </a>
                            </div>
                        ))}
                    </div>
                )}
            </main>
        </div>
    );
};

export default PainelPage;
